import React, { useState } from 'react';
import {
  PrimaryButton,
  DefaultButton,
  Stack,
  Text,
  Separator,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import { SEOHead } from '../components/SEO/SEOHead';
import {
  InstructionsContainer,
  H1,
  H2,
  H4,
  P,
  IM,
  TextCenterContainer,
} from './Pages-styles';
import AdminContentPanel from '../components/AdminContentPanel';
import { useHomeContext } from '../hooks/useHomeContext';
import { useQuery } from '@apollo/client';
import { ME } from '../graphql/queries';

const AccordionItem: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      style={{
        marginBottom: '5px',
        border: '0px #ffffff',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
    <SEOHead
      title="СметаFORTE - Главная"
      description="СметаFORTE - профессиональный модуль для сметного дела. Сопоставительные ведомости, управление проектами, автоматизация расчетов."
      keywords={['смета', 'СВОР', 'Ведомость', 'ведомость', 'свор', 'работы', 'сметы', 'сопоставительная ведомость объёмов работ', 'цэс', 'свор быстро', 'автоматически', 'объёмы работ', 'программый модуль', '', '', '', 'строительство', 'автоматизация', 'ГРАНД-Смета', 'СметаFORTE']}
    />
      <DefaultButton
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '20px 16px',
          background: '#ffffff',
          justifyContent: 'space-between',
          fontWeight: 600,
          fontSize: '16px', 
          border: 'none',
          borderBottom: '#ffffff',
          textAlign: 'left',
        }}
        iconProps={{ iconName: isOpen ? 'ChevronDown' : 'ChevronRight' }}
      >
        {title}
      </DefaultButton>
      {isOpen && (
        <div
          style={{
            background: '#ffffff',
            padding: '16px',
            fontSize: '18px',
            lineHeight: '1.6',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const CvorPage: React.FC = () => {
  const { data: meData } = useQuery(ME);
  const userRole = meData?.me?.role || null;

  const {
    activeContent,
    loading,
    canEdit,
    availableNotes,
    setAsHomepage,
    deleteArticle,
    saveVideo,
    clearVideo,
  } = useHomeContext(userRole);

  const API_URL = process.env.REACT_APP_API_URL || 'https://sd.smetaforte.ru/api';

  return (
    <InstructionsContainer id="main-page">
      
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '0 auto', padding: '20px' } }}>
        <H1 id="main-page-h1">Программный модуль Смета FORTE</H1>
        <H2 id="main-page-h2">Сопоставительная ведомость объемов работ (СВОР)</H2>
        <H4>Авторская разработка ООО «ЦЭС»</H4>
      </Stack>
      <Separator />

      <AdminContentPanel
        activeContent={activeContent}
        loading={loading}
        canEdit={canEdit}
        availableNotes={availableNotes}
        onSaveArticle={setAsHomepage}
        onDeleteArticle={deleteArticle}
        onSaveVideo={saveVideo}
        onClearVideo={clearVideo}
        apiURL={API_URL}
        contentMode="fixed"
      />

      {/* ЧТО ЭТО ТАКОЕ? */}
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '40px auto 0', padding: '20px' } }}>
        <H2>Что такое «СметаFORTE»?</H2>
        <P>
          «СметаFORTE» — это специализированная надстройка для Microsoft Excel, которая автоматически формирует 
          сопоставительные ведомости (СВОР и СВИСС) и другие сметные документы на основе ваших файлов из сметных программ.
        </P>
        <P>
          Вам больше не нужно вручную сверять сотни позиций в сметах, искать различия в объемах и ценах, 
          заполнять таблицы по Методике 421/пр. Модуль сделает это за вас за 5 минут, исключив ошибки 
          ручного ввода и сэкономив часы работы.
        </P>
        
        <Text variant="xLarge" block style={{ fontWeight: 'bold', fontSize: '20px', color: '#0078d4' }}>
          Это необходимо, если вы работаете с проектной и рабочей документацией, проходите экспертизу 
          или исполняете контракты, где требуется обоснование изменений объемов и стоимости работ.
        </Text>
      </Stack>

      {/* ДЛЯ КОГО ЭТОТ МОДУЛЬ - белый фон, больше воздуха сверху */}
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '60px auto 0', padding: '20px', background: '#ffffff' } }}>
        <H2>Для кого предназначен модуль?</H2>
        <Stack horizontal wrap tokens={{ childrenGap: 20 }}>
          <Stack styles={{ root: { flex: 1, minWidth: 250 } }}>
            <Text variant="large" style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 8 }}>
              Сметчикам и экономистам
            </Text>
            <P style={{ fontSize: '16px' }}>
              Автоматизация рутинных операций по сравнению смет и формированию ведомостей. 
              Больше времени на анализ, меньше — на механическую работу.
            </P>
          </Stack>
          <Stack styles={{ root: { flex: 1, minWidth: 250 } }}>
            <Text variant="large" style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 8 }}>
              Проектным организациям
            </Text>
            <P style={{ fontSize: '16px' }}>
              Быстрое формирование СВОР при корректировке проектной документации 
              и переходе от стадии «П» к стадии «Р».
            </P>
          </Stack>
          <Stack styles={{ root: { flex: 1, minWidth: 250 } }}>
            <Text variant="large" style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 8 }}>
              Подрядчикам и Заказчикам
            </Text>
            <P style={{ fontSize: '16px' }}>
              Обоснование изменений объемов работ при исполнении контрактов. 
              Прозрачность и документальное подтверждение всех изменений.
            </P>
          </Stack>
        </Stack>
      </Stack>

      {/* ОСНОВНЫЕ ЗАДАЧИ - голубой и белый вместо зеленого */}
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '60px auto 0', padding: '20px' } }}>
        <H2>Какие задачи решает модуль?</H2>
        <Stack tokens={{ childrenGap: 12 }}>
          {[
            'Автоматическое сравнение двух и более локальных сметных расчетов (ЛСР)',
            'Выявление расхождений в объемах, ценах и коэффициентах',
            'Формирование СВОР и СВИСС строго по форме Приложения №12 к Методике 421/пр',
            'Подготовка ЛСР+ (включаемые позиции) и ЛСР- (исключаемые позиции)',
            'Создание ведомостей объемов работ (ВОР) в натуральных единицах измерения',
            'Формирование коммерческих и исполнительных смет',
            'Экспорт результатов в Excel и обратно в ГРАНД-Смету (формат .xml)',
          ].map((item, index) => (
            <MessageBar
              key={index}
              messageBarType={MessageBarType.info}
              isMultiline={false}
              styles={{
                root: {
                  '::before': { backgroundColor: 'transparent !important' },
                  background: '#ffffff',
                  border: '1px solid #cce4f7',
                  borderRadius: '4px',
                  padding: '8px 12px',
                },
                icon: { color: '#0078d4', fontSize: 18 },
                text: { color: '#0078d4', fontWeight: 500, fontSize: '16px' },
              }}
            >
              {item}
            </MessageBar>
          ))}
        </Stack>
      </Stack>

      {/* С КАКИМИ ФОРМАТАМИ РАБОТАЕТ - белый фон, больше воздуха сверху */}
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '60px auto 0', padding: '20px', background: '#ffffff' } }}>
        <H2>С какими сметными программами работает?</H2>
        <P style={{ fontSize: '16px' }}>
          Модуль читает файлы из всех популярных сметных программ России:
        </P>
        <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
          {[
            'ГРАНД-Смета (.gsfx, .xml)',
            'Smeta.ru (.xml)',
            'АтомСмета (.xml)',
            'Главгосэкспертиза (.gge)',
            'Мосгосэкспертиза (.mge)',
          ].map((format, index) => (
            <Text 
              key={index} 
              style={{ 
                background: '#ffffff', 
                padding: '8px 16px', 
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                fontSize: '16px',
                fontWeight: 500,
              }}
            >
              {format}
            </Text>
          ))}
        </Stack>
        <P style={{ fontSize: '16px', marginTop: 12 }}>
          Поддерживаются ЛСР по Методике 421/пр (РИМ и БИМ с построчной индексацией), 
          а также ТСН для города Москвы.
        </P>
      </Stack>

      {/* Изображение */}
      <IM src="/SopVedomost.webp" alt="Образец Сопоставительной ведомости объемов работ" />

      {/* Инструкция */}
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '40px auto', padding: '20px', textAlign: 'center' } }}>
        <H2>Как начать работу?</H2>
        <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 16 }} wrap styles={{ root: { marginTop: 8 } }}>
          <PrimaryButton
            text="Скачать инструкцию (PDF)"
            iconProps={{ iconName: 'Download' }}
            onClick={() =>
              window.open('https://drive.google.com/file/d/1mvWfYQB7CoOiCveqdcIzFLGJZsl1vulr/view', '_blank')
            }
            style={{ minWidth: 350, fontSize: '16px', height: 48 }}
          />
          <DefaultButton
            text="Смотреть видео на Rutube"
            iconProps={{ iconName: 'Play' }}
            onClick={() =>
              window.open('https://rutube.ru/video/6618c961e2abe0089483f33644f498ab/', '_blank')
            }
            style={{ minWidth: 350, fontSize: '16px', height: 48 }}
          />
        </Stack>
      </Stack>
      
      <Separator />

      {/* ВОЗМОЖНОСТИ И ПРЕИМУЩЕСТВА */}
      <TextCenterContainer>
        <H2>Возможности и преимущества СВОР</H2>
      </TextCenterContainer>

      <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: '0 20px 20px', maxWidth: '900px', margin: '0 auto' } }}>
        <AccordionItem
          title="Полностью автоматизированное формирование СВОР и СВИСС за 5 минут"
        >
          <P>
            В отличие от ручного подхода, модуль самостоятельно обрабатывает исходные данные: объемы работ, 
            их изменения, цены и коэффициенты, структурируя их в таблицу за считанные минуты. 
            Это критически важно при больших объемах данных, где цена ошибки ручного ввода слишком высока.
          </P>
        </AccordionItem>

        <AccordionItem
          title="Поддержка всех популярных форматов сметных программ"
        >
          <P>
            Вам не нужно перепечатывать данные. Модуль напрямую читает файлы форматов: 
            .gsfx и .xml (ГРАНД-Смета), .xml (Smeta.ru, АтомСмета), .gge (Главгосэкспертиза) и .mge (Мосгосэкспертиза).
          </P>
        </AccordionItem>

        <AccordionItem title="Автоматизированное формирование полного комплекта документов">
          <ul style={{ margin: '12px 0', paddingLeft: '20px', fontSize: '16px', lineHeight: '1.6' }}>
            <li>Сопоставительная ведомость объемов работ (СВОР)</li>
            <li>Сопоставительная ведомость изменения сметной стоимости (СВИСС)</li>
            <li>Ведомость объемов работ (ВОР П, ВОР Р, ВОР+, ВОР-)</li>
            <li>Локальный сметный расчет (ЛСР) по Методике 2020 для РИМ/БИМ (для Сметы П и Р)</li>
            <li>Локальный сметный расчет+ (ЛСР+) и Локальный сметный расчет– (ЛСР-)</li>
            <li>Коммерческая и исполнительная сметы</li>
          </ul>
        </AccordionItem>

        <AccordionItem title="Автоматический перенос ссылок на чертежи и комментариев из смет">
          <P>
            Если в исходном файле (например, из ГРАНД-Сметы) в комментариях к позиции указана ссылка на чертеж, 
            модуль автоматически перенесет её в графу «Ссылка на техническую документацию» в СВОР/СВИСС. 
            Дата составления сметы также переносится автоматически, экономя время на ручное заполнение.
          </P>
        </AccordionItem>

        <AccordionItem title="Экспорт в Excel и обратная совместимость с ГРАНД-Сметой">
          <P>
            Готовые документы можно выгрузить в Excel для удобной проверки. 
            Более того, сформированные ЛСР+ и ЛСР- можно экспортировать обратно в формат .xml 
            для дальнейшего редактирования уже внутри вашей сметной программы.
          </P>
        </AccordionItem>
      </Stack>

      <Separator />

      {/* ТАРИФНАЯ ПОЛИТИКА */}
      <TextCenterContainer>
        <H2>Тарифная политика</H2>
        <P>Программное обеспечение распространяется по модели SaaS (подписка).<br/>
        Технические требования: Windows 10/11, Excel 2021 и выше, 4 ГБ ОЗУ, доступ в Интернет.</P>
      </TextCenterContainer>

      <Stack
        horizontal
        wrap
        horizontalAlign="center"
        tokens={{ childrenGap: 20 }}
        styles={{ root: { padding: '20px 30px', maxWidth: '800px', margin: '0 auto' } }}
      >
        <Stack 
          styles={{ 
            root: { 
              background: '#ffffff',
              padding: '24px', 
              borderRadius: '8px', 
              width: '320px', 
              textAlign: 'center',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            } 
          }}
          tokens={{ childrenGap: 12 }}
        >
          <Text variant="xLarge" style={{ fontWeight: 'bold' }}>Тариф «Все включено»</Text>
          <Text variant="large" style={{ color: '#605e5c', fontSize: '16px' }}>6 месяцев</Text>
          <Text variant="xxLarge" style={{ fontWeight: 'bold', color: '#0078d4', fontSize: '28px' }}>14 000 руб.</Text>
          <Separator styles={{ root: { margin: '12px 0' } }} />
          <Text style={{ fontSize: '16px', color: '#605e5c' }}>Техническая поддержка и обучение включены</Text>
        </Stack>

        <Stack 
          styles={{ 
            root: { 
              background: '#ffffff',
              padding: '24px', 
              borderRadius: '8px', 
              width: '320px', 
              textAlign: 'center',
              border: '2px solid #0078d4',
              boxShadow: '0 4px 8px rgba(0,120,212,0.15)',
            } 
          }}
          tokens={{ childrenGap: 12 }}
        >
          <Text variant="xLarge" style={{ fontWeight: 'bold' }}>Тариф «Все включено»</Text>
          <Text variant="large" style={{ color: '#605e5c', fontSize: '16px' }}>1 год</Text>
          <Text variant="xxLarge" style={{ fontWeight: 'bold', color: '#0078d4', fontSize: '28px' }}>24 500 руб.</Text>
          <Text style={{ fontSize: '16px', color: '#107c10', fontWeight: 'bold' }}>Выгода 3 500 руб.</Text>
          <Separator styles={{ root: { margin: '12px 0' } }} />
          <Text style={{ fontSize: '16px', color: '#605e5c' }}>Техническая поддержка и обучение включены</Text>
        </Stack>
      </Stack>

      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { maxWidth: '800px', margin: '0 auto 40px', textAlign: 'center' } }}>
        <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>Условия оплаты:</Text>
        <Text style={{ fontSize: '16px' }}>
          Оплата производится по истечении срока подписки. Доступные способы: банковский перевод или оплата картой.
        </Text>
      </Stack>

      <Separator />

      {/* ТЕХНИЧЕСКИЕ ТРЕБОВАНИЯ */}
      <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '900px', margin: '0 auto', padding: '20px' } }}>
        <H2>Технические требования</H2>
        <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
          {[
            'Microsoft Excel 2021 и выше',
            'Windows 10/11 (32 или 64-bit)',
            'Процессор: 2 ядра, от 2 ГГц',
            'ОЗУ: 4 ГБ (64-bit) / 2 ГБ (32-bit)',
            'Интернет-соединение',
          ].map((req, index) => (
            <Text 
              key={index}
              style={{ 
                background: '#faf9f8', 
                padding: '8px 16px', 
                borderRadius: '4px',
                fontSize: '16px',
                border: '1px solid #f0f0f0',
              }}
            >
              {req}
            </Text>
          ))}
        </Stack>
      </Stack>

      {/* КОНТАКТЫ И ПОДДЕРЖКА */}
      <TextCenterContainer>
        <H2>Контакты и поддержка</H2>
        <P style={{ fontSize: '16px' }}>
          Для заказа демонстрации, получения консультации или технической поддержки:
        </P>
        <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 32 }} wrap styles={{ root: { marginTop: 16 } }}>
          <Text variant="large" style={{ fontSize: '18px' }}>
            <a href="tel:+78312171737" style={{ color: '#0078d4', textDecoration: 'none' }}>+7 (831) 217-17-37</a>
          </Text>
          <Text variant="large" style={{ fontSize: '18px' }}>
            <a href="https://www.cesnnov.ru/cvorcvicc" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', textDecoration: 'none' }}>www.cesnnov.ru</a>
          </Text>
          {/* <Text variant="large" style={{ fontSize: '18px' }}>
            <a href="https://t.me/+LVAJqmLtNspiZDNi" target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', textDecoration: 'none' }}>Max</a>
          </Text> */}
        </Stack>
      </TextCenterContainer>

    </InstructionsContainer>
  );
};

export default CvorPage;